// Direct imports
import { Behavior } from "@babylonjs/core/Behaviors/behavior";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { Matrix } from "@babylonjs/core/Maths/math.vector";
import { Scene } from "@babylonjs/core/scene";
import { BehaviorData, ValidationError } from '../scenedata/behaviordata';
import { TRANSFORM_PATH_KEYWORDS } from '../scenedata/common';
import type { SceneObject } from '../core/SceneObject';
import type { ResourceManager } from '../loaders/ResourceManager';
import { Logger } from '../utils/Logger';
import { markBakeDirty } from '../loaders/restoreBakedSrc';
import {
    compileExpression,
    evaluateExpression,
    extractExpressionIdentifiers,
    EXPRESSION_BUILTINS,
    inspectExpression,
} from '../core/canopy/ExpressionEvaluator';

// 2024
// Author: Aaron Post

// ── Property reference patterns ──
//
// Two syntax forms are supported:
//   Shorthand:  $name                      — simple property lookup (whole-field only)
//   Expression: {name} or {expr}           — lookup or JS expression syntax
//
// Simple lookups are type-preserving. Expressions evaluate via `new Function()`.
// See docs/canopy/CANOPY_EXPRESSIONS.md for the expression system spec.

// Whole-field: "$name" or "{name}" — entire string is a single reference (type-preserving)
export const WHOLE_REF_RE = /^(?:\$([a-zA-Z_][a-zA-Z0-9_]*)|\{([a-zA-Z_][a-zA-Z0-9_]*)\})$/;

// Whole-field expression: "{...}" where contents are NOT a simple identifier
// Captures the expression body between { and }. Rejects escaped {{ at start
// and }} at end. Uses [^{}] to prevent matching nested/multiple brace pairs.
export const WHOLE_EXPR_RE = /^\{([^{}]+)\}$/;

// Interpolation: "{name}" — embedded within a string (always produces string)
// Matches bare { } form. Escaped {{ is handled in the resolver.
const INTERPOLATION_RE = /(?<!\{)\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

// Expression interpolation: "{expr}" where expr is not a simple identifier
// Matches balanced { } with complex contents (dots, operators, parens, etc.)
// Uses a non-greedy match — may need refinement for nested parens
const EXPR_INTERPOLATION_RE = /(?<!\{)\{([^}]*[^a-zA-Z0-9_}][^}]*)\}/g;

/** Extract the captured name from a WHOLE_REF_RE match (group 1 for $name, group 2 for {name}) */
export function wholeRefName(match: RegExpMatchArray): string {
    return match[1] ?? match[2];
}

/**
 * True if a string field value is a property binding rather than a literal:
 * a whole-field reference (`$name` / `{name}`), a whole-field expression
 * (`{expr}`), or a string containing `{...}` interpolation.
 *
 * Used by consumers that must not treat authored binding strings as literal
 * values — e.g. the export pipeline skipping `src: "$material"` when
 * collecting/validating asset URLs, and the Renderer guarding against
 * loading an unresolved binding as a URL.
 */
export function isPropertyBinding(value: string): boolean {
    return WHOLE_REF_RE.test(value) || /(?<!\{)\{[^{}]+\}/.test(value);
}

/**
 * The property name a whole-field reference binds to (`"$material"` /
 * `"{material}"` → `"material"`), or null for literals and expressions.
 * Editors use this to surface bound fields meaningfully — e.g. show the
 * bound property's resolved value instead of the raw binding string.
 */
export function boundPropertyName(value: unknown): string | null {
    const match = typeof value === 'string' ? value.match(WHOLE_REF_RE) : null;
    return match ? wholeRefName(match) : null;
}

/** Check if an expression body is a simple identifier (no dots, operators, etc.) */
function _isSimpleIdentifier(expr: string): boolean {
    return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(expr);
}

/**
 * Whether a binding opens inside another: `{a + {b ? 1 : 0}}`. The inner one
 * resolves on its own and leaves the outer as text, so the field never gets
 * a value. `{{` and `}}` outside a binding are escaped literal braces.
 */
function _hasNestedBinding(value: string): boolean {
    let open = false;
    for (let i = 0; i < value.length; i++) {
        const c = value[i];
        if (c !== '{' && c !== '}') continue;
        if (!open && value[i + 1] === c) { i++; continue; }
        if (c === '{' && open) return true;
        open = c === '{';
    }
    return false;
}

/** Value types that properties can hold */
export type PropertyValue = string | number | boolean | [number, number, number] | [number, number, number, number];

export interface BindingDiagnostic {
    object: string;
    behavior: string;
    field: string;
    source: string;
    code: 'missing-property' | 'invalid-expression' | 'evaluation-error' | 'unresolved-value' | 'non-finite' | 'invalid-transform' | 'nested-braces';
    message: string;
}

/**
 * Interface for entities that can subscribe to property changes.
 * Implemented by both TraceBehavior and SceneObject.
 */
export interface PropertySubscriber {
    _onPropertyChanged(name: string): void;
    _resubscribeAndResolve(): void;
}

/** Check if two sets contain the same elements */
function _setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
    if (a.size !== b.size) return false;
    for (const item of a) {
        if (!b.has(item)) return false;
    }
    return true;
}

/**
 * Interface for property providers (implemented by Properties behavior).
 * Defined here to avoid circular imports — TraceBehavior needs to subscribe
 * to property changes without importing Properties directly.
 */
export interface PropertyProvider {
    hasProperty(name: string): boolean;
    /** `settled` reads the target of an in-flight transition instead of the eased overlay. */
    getValue(name: string, settled?: boolean): PropertyValue | undefined;
    /** Base declaration value + every authored state's override, deduplicated, in authoring order. */
    getDeclaredValues(name: string): PropertyValue[];
    subscribe(name: string, subscriber: PropertySubscriber): void;
    unsubscribe(name: string, subscriber: PropertySubscriber): void;
    unsubscribeAll(subscriber: PropertySubscriber): void;
}

/**
 * Walk up from `start` to the first Properties behavior declaring `name`.
 * The single source of truth for "which provider defines this property" —
 * used by the subscription wiring below and by consumers that need the
 * provider itself (e.g. Renderer's bound-asset prefetch).
 */
export function findPropertyProvider(start: SceneObject, name: string): PropertyProvider | null {
    let current: SceneObject | null = start;
    while (current) {
        const props = current.getBehavior('Properties');
        if (props?.hasProperty(name)) return props;
        current = current.getParent();
    }
    return null;
}

/**
 * Abstract base class for all Trace behaviors.
 * Implements Babylon.js Behavior<TransformNode> interface.
 *
 * Handles attachedNode management required by Babylon.js 8.39+
 * Subclasses should override onAttach() and onDetach() for custom logic.
 *
 * ## Typed API
 *
 * Behaviors own their data via `_resolved` (runtime-ready values):
 * - `getData()` - Returns a defensive copy of the authored data (with `$refs`)
 * - `commit(data)` - Validates and applies changes, returns errors
 * - `serialize()` - Returns validated copy for export
 * - `validate(data)` - Override for behavior-specific validation/migration
 *
 * ## Dual-data model
 *
 * - `_authored` (private) — authored data, exactly as written. May contain property
 *   references (`$name`, `${name}`, or `{name}`). This is what `serialize()` returns
 *   and `commit()` writes to.
 *   Subclasses that need authored data (e.g. Properties) use `getAuthored()`.
 * - `_resolved` (protected) — derived from `_authored` with all property references
 *   replaced by their resolved values. This is what subclasses read at runtime.
 */
export abstract class TraceBehavior<TData extends BehaviorData = BehaviorData> implements Behavior<TransformNode>, PropertySubscriber {
    /** Babylon.js Behavior name — derived from the data's @type discriminator */
    public get name(): string { return this._authored['@type']; }
    public readonly sceneObject: SceneObject;

    /**
     * Authored data — the single source of truth for serialization and commits.
     * May contain property references (`$name`, `${name}`, `{name}`). Managed by the base class; subclasses that
     * need raw authored data should use `getAuthored()`.
     * @private
     */
    private _authored!: TData;

    /**
     * Resolved view of the authored data — all property references (`$name`, `${name}`, `{name}`)
     * replaced with their resolved property values. This is the primary data
     * subclasses should read at runtime (e.g. `this._resolved.speed`).
     * @protected
     */
    protected _resolved!: TData;

    /**
     * Set of property names referenced in authored data via `$name`, `${name}`, or `{name}` syntax.
     * Empty when no references exist (the common case for existing behaviors).
     */
    private _propertyNames: Set<string> = new Set();

    /** True if authored data contains `{{` escaped braces that need unescaping during resolution */
    private _hasEscapedBraces = false;

    /**
     * Opt out of property-reference resolution entirely — `_resolved` is a
     * verbatim copy of authored data and no property subscriptions are made.
     *
     * For behaviors whose fields hold arbitrary human text rather than authored
     * values (see {@link Provenance}). `$name` and `{name}` are ordinary
     * characters in a sentence, and a behavior that stores sentences must not
     * have them read as bindings.
     *
     * Read from a `static literalData = true` on the constructor, the same way
     * `cmsManaged` is read in `core/SceneObject.ts`.
     */
    private get _literalData(): boolean {
        return (this.constructor as { literalData?: boolean }).literalData === true;
    }

    /** Re-scan authored data for property references. Honors {@link _literalData}. */
    private _rescanPropertyNames(): void {
        this._propertyNames = this._literalData
            ? new Set()
            : TraceBehavior._scanPropertyNames(this._authored);
        this._hasEscapedBraces = this._literalData
            ? false
            : TraceBehavior._containsEscapedBraces(this._authored);
    }

    /** The node this behavior is attached to (managed automatically) */
    public attachedNode: TransformNode | null = null;

    constructor(sceneObject: SceneObject, data: TData) {
        this.sceneObject = sceneObject;
        // Clone the data to ensure behaviors have their own mutable copy
        // This prevents errors when data comes from frozen Zustand/Immer state
        this._authored = structuredClone(data);
        this._rescanPropertyNames();
        // Resolve property refs immediately — sceneObject.resolveProperty() can walk
        // the ancestor tree as soon as the SceneObject exists in the hierarchy.
        // This ensures load() reads resolved values, not raw "$name" strings.
        this._resolved = this._resolveAll();
    }

    // ── Property reference scanning and resolution ──

    /**
     * Recursively scan a data object for property references (`$name`, `${name}`, `{name}`).
     * Returns the set of referenced property names.
     */
    static _scanPropertyNames(data: unknown): Set<string> {
        const names = new Set<string>();
        TraceBehavior._walkForRefs(data, names);
        return names;
    }

    /** Check if data contains `{{` or `}}` escaped braces anywhere (needs unescaping even with no refs) */
    private static _containsEscapedBraces(value: unknown): boolean {
        if (typeof value === 'string') return value.includes('{{') || value.includes('}}');
        if (Array.isArray(value)) return value.some(item => TraceBehavior._containsEscapedBraces(item));
        if (value !== null && typeof value === 'object') {
            return Object.values(value as Record<string, unknown>).some(v => TraceBehavior._containsEscapedBraces(v));
        }
        return false;
    }

    /**
     * Recursive walker for _scanPropertyNames.
     * Note: Uses INTERPOLATION_RE's lookbehind (?<!\{) to skip escaped `{{` —
     * this is correct without placeholder substitution because the lookbehind
     * rejects `{` preceded by `{`. The resolver (_resolveValue) uses placeholder
     * substitution instead because it needs to restore escaped braces in the output.
     */
    private static _walkForRefs(value: unknown, names: Set<string>): void {
        if (typeof value === 'string') {
            // 1. Check whole-field simple ref: "$name" or "{name}"
            const wholeMatch = value.match(WHOLE_REF_RE);
            if (wholeMatch) {
                names.add(wholeRefName(wholeMatch));
                return;
            }
            // 2. Check whole-field expression: "{camera.distance / 10}"
            const exprMatch = value.match(WHOLE_EXPR_RE);
            if (exprMatch) {
                for (const id of extractExpressionIdentifiers(exprMatch[1])) {
                    names.add(id);
                }
                return;
            }
            // 3. Check inline interpolation: simple refs and expressions
            //    Strip escaped {{ / }} before scanning to prevent false matches
            const stripped = value.replaceAll('{{', '\u0000\u0000').replaceAll('}}', '\u0001\u0001');
            let m: RegExpExecArray | null;
            // Simple refs: {name}
            INTERPOLATION_RE.lastIndex = 0;
            while ((m = INTERPOLATION_RE.exec(stripped)) !== null) {
                names.add(m[1]);
            }
            // Expression refs: {camera.distance / 10} inline
            EXPR_INTERPOLATION_RE.lastIndex = 0;
            while ((m = EXPR_INTERPOLATION_RE.exec(stripped)) !== null) {
                for (const id of extractExpressionIdentifiers(m[1])) {
                    names.add(id);
                }
            }
            return;
        }
        if (Array.isArray(value)) {
            for (const item of value) {
                TraceBehavior._walkForRefs(item, names);
            }
            return;
        }
        if (value !== null && typeof value === 'object') {
            for (const key of Object.keys(value)) {
                TraceBehavior._walkForRefs((value as Record<string, unknown>)[key], names);
            }
        }
    }

    /**
     * Resolve all property references in authored data, producing `_resolved`.
     * Supports `$name` and `{name}` / `{expression}` syntax.
     * Uses `sceneObject.resolveProperty()` for ancestor tree walk.
     */
    protected _resolveAll(): TData {
        if (this._propertyNames.size === 0 && !this._hasEscapedBraces) {
            return structuredClone(this._authored);
        }
        return TraceBehavior._resolveValue(this._authored, this.sceneObject) as TData;
    }

    /** Recursive resolver — produces a new object with $refs replaced */
    static _resolveValue(value: unknown, sceneObject: SceneObject): unknown {
        if (typeof value === 'string') {
            // 1. Whole-field simple reference: "$name" or "{name}" — type-preserving
            const wholeMatch = value.match(WHOLE_REF_RE);
            if (wholeMatch) {
                const name = wholeRefName(wholeMatch);
                const resolved = sceneObject.resolveProperty(name);
                return resolved !== undefined ? resolved : value;
            }
            // 2. Whole-field expression: "{camera.distance / 10}" — type-preserving
            const exprMatch = value.match(WHOLE_EXPR_RE);
            if (exprMatch && !_isSimpleIdentifier(exprMatch[1])) {
                return TraceBehavior._evaluateExpr(exprMatch[1], sceneObject) ?? value;
            }
            // 3. Interpolation — always string
            if (value.includes('{')) {
                // Unescape {{ and }} via placeholders, resolve refs/exprs, then restore
                const escaped = value.replaceAll('{{', '\u0000').replaceAll('}}', '\u0001');
                // First pass: simple refs ({name})
                INTERPOLATION_RE.lastIndex = 0;
                let result = escaped.replace(INTERPOLATION_RE, (match, name: string) => {
                    const resolved = sceneObject.resolveProperty(name);
                    if (resolved !== undefined) {
                        return typeof resolved === 'object' ? JSON.stringify(resolved) : String(resolved);
                    }
                    return match;
                });
                // Second pass: expression refs ({camera.distance / 10})
                EXPR_INTERPOLATION_RE.lastIndex = 0;
                result = result.replace(EXPR_INTERPOLATION_RE, (match, expr: string) => {
                    const evaluated = TraceBehavior._evaluateExpr(expr, sceneObject);
                    if (evaluated !== undefined) {
                        return typeof evaluated === 'object' ? JSON.stringify(evaluated) : `${evaluated as string | number | boolean}`;
                    }
                    return match;
                });
                return result.replaceAll('\u0000', '{').replaceAll('\u0001', '}');
            }
            return value;
        }
        if (Array.isArray(value)) {
            return value.map(item => TraceBehavior._resolveValue(item, sceneObject));
        }
        if (value !== null && typeof value === 'object') {
            const result: Record<string, unknown> = {};
            for (const key of Object.keys(value)) {
                result[key] = TraceBehavior._resolveValue((value as Record<string, unknown>)[key], sceneObject);
            }
            return result;
        }
        // Primitives (number, boolean, null) pass through
        return value;
    }

    /**
     * Build an expression scope from a SceneObject's property tree and evaluate.
     * The scope contains: system values + built-in functions + resolved properties.
     * User properties shadow system values (higher priority).
     * Returns undefined if compilation or evaluation fails.
     */
    private static _evaluateExpr(expr: string, sceneObject: SceneObject): unknown {
        const fn = compileExpression(expr);
        if (!fn) return undefined;
        return evaluateExpression(fn, TraceBehavior._expressionScope(expr, sceneObject));
    }

    private static _expressionScope(expr: string, sceneObject: SceneObject): Record<string, unknown> {
        // Build scope: system values (lowest priority) → builtins → user properties (highest)
        const systemValues = sceneObject.traceScene?.getSystemValues();
        const scope: Record<string, unknown> = {
            ...(systemValues ? systemValues.buildScope(sceneObject.transform) : {}),
            ...EXPRESSION_BUILTINS,
        };

        // Extract identifiers and resolve each against the property tree.
        // User properties overwrite system values with the same name.
        for (const id of extractExpressionIdentifiers(expr)) {
            const resolved = sceneObject.resolveProperty(id);
            if (resolved !== undefined) {
                scope[id] = resolved;
            }
        }

        return scope;
    }

    /** Inspect authored binding fields without editing scene data.
     * Includes inactive state fields; consumers must not call them active failures.
     */
    public getBindingDiagnostics(limit = 50): BindingDiagnostic[] {
        if (this._literalData) return [];
        const diagnostics: BindingDiagnostic[] = [];
        const object = this.sceneObject;
        const walk = (value: unknown, field: string): void => {
            if (diagnostics.length >= limit) return;
            if (Array.isArray(value)) { value.forEach((item, index) => walk(item, `${field}[${index}]`)); return; }
            if (value && typeof value === 'object') { for (const [key, item] of Object.entries(value)) walk(item, `${field}.${key}`); return; }
            if (typeof value !== 'string') return;
            // `$self` / `$parent` / `$root` are TransformPath keywords wherever they
            // appear whole (graph targets, camera-view and follow targets), not
            // references to properties of those names. The schema types such slots
            // as plain strings, so the value is the only reliable signal.
            if (TRANSFORM_PATH_KEYWORDS.has(value)) return;
            if (_hasNestedBinding(value)) {
                diagnostics.push({ object: object.getPath(), behavior: this.name, field, source: value, code: 'nested-braces',
                    message: 'A binding opens inside another, so neither evaluates: write one {…} and group with parentheses, e.g. {a + (b ? 1 : 0)}' });
                return;
            }
            const whole = value.match(WHOLE_REF_RE);
            // Escaped braces are literal content, just as in _resolveValue.
            const unescaped = value.replaceAll('{{', '\u0000').replaceAll('}}', '\u0001');
            const expressions = whole ? [wholeRefName(whole)] : [...unescaped.matchAll(/\{([^{}]+)\}/g)].map(match => match[1]);
            for (const expression of expressions) {
                if (diagnostics.length >= limit) break;
                let resolved: unknown;
                let problem: Pick<BindingDiagnostic, 'code' | 'message'> | undefined;
                if (_isSimpleIdentifier(expression)) {
                    resolved = object.resolveProperty(expression);
                    if (resolved === undefined) problem = { code: 'missing-property', message: `No property named "${expression}" resolves from this object` };
                } else {
                    const result = inspectExpression(expression, TraceBehavior._expressionScope(expression, object));
                    if (result.ok) resolved = result.value;
                    else problem = { code: result.code, message: result.message };
                }
                if (!problem && (resolved === undefined || resolved === null)) problem = { code: 'unresolved-value', message: 'Binding evaluated to no value' };
                if (!problem && typeof resolved === 'number' && !Number.isFinite(resolved)) problem = { code: 'non-finite', message: 'Binding evaluated to NaN or infinity' };
                if (!problem && /\.transform\.(position|rotation|scale)\[\d+\]$/.test(field) && !Number.isFinite(Number(resolved))) problem = { code: 'invalid-transform', message: 'Transform component did not resolve to a finite number' };
                if (problem) diagnostics.push({ object: object.getPath(), behavior: this.name, field, source: value, ...problem });
            }
        };
        walk(this._authored, '$');
        return diagnostics;
    }

    // ── Data access ──

    /**
     * Access the authored data (may contain `$name` references).
     * Most subclasses should read `_resolved` instead — this is only needed by
     * Properties for building its lookup cache from the
     * original property definitions.
     */
    protected getAuthored(): TData { return this._authored; }

    /**
     * Replace authored data and re-resolve.
     * Use sparingly — prefer `commit()` for normal data updates.
     * This exists for cases where subclasses need to bypass validation
     * (e.g. in-memory-only animation selection state).
     */
    protected _replaceAuthored(data: TData): void {
        this._authored = data;
        this._rescanPropertyNames();
        this._resolved = this._resolveAll();
    }

    /**
     * Get a defensive copy of authored behavior data.
     * Modifications to the returned object will not affect the behavior.
     * Use commit() to apply changes.
     *
     * @returns A copy of the authored behavior data (may contain $refs)
     */
    public getData(): TData {
        return structuredClone(this._authored);
    }

    /**
     * Read-only view of the resolved data (property refs and `{expression}`
     * bindings substituted). For runtime consumers that must see live values —
     * e.g. the layout bridge reads sibling Layout/LayoutItem sizing through
     * this so a bound field like `width: "{w}"` solves as a number, not NaN.
     * Do not mutate; use commit() for changes.
     * @internal
     */
    public _getResolvedData(): Readonly<TData> {
        return this._resolved;
    }

    /**
     * Commit a partial patch to behavior data.
     * Merges patch into authored data (shallow Object.assign), validates, and applies side effects.
     *
     * @param patch - Partial data to merge (top-level keys replace, unmentioned keys preserved)
     * @returns Array of validation errors (empty on success)
     *
     * @example
     * ```typescript
     * // Targeted edit — only send what changed
     * const errors = await behavior.commit({ speed: 2.5 });
     *
     * // Full snapshot — still works (e.g. undo/redo)
     * const errors = await behavior.commit(fullSnapshot);
     * ```
     */
    public async commit(patch: Partial<TData>): Promise<ValidationError[]> {
        return this._applyCommit(patch, /* replace */ false);
    }

    /**
     * Restore authored data from a full snapshot — replaces every authored
     * field with the snapshot's value, including dropping keys that exist
     * in the current authored data but are missing from the snapshot.
     * Runs the same validation + applyCommit pipeline as `commit()`.
     *
     * This is the path BehaviorCommitCommand uses for undo/redo: a
     * shallow-merge `commit(snapshot)` is asymmetric — fields added by the
     * forward edit don't get removed on undo, because Object.assign doesn't
     * iterate keys missing from the patch. Snapshot restoration must.
     *
     * Callers outside the undo system should prefer `commit()` for normal
     * partial-patch updates.
     *
     * @internal — intended for the editor's undo system. Other callers
     * should use `commit()` for partial patches.
     * @param snapshot - Full authored data to install
     * @returns Array of validation errors (empty on success)
     */
    public async commitSnapshot(snapshot: TData): Promise<ValidationError[]> {
        return this._applyCommit(snapshot, /* replace */ true);
    }

    private async _applyCommit(
        patch: Partial<TData> | TData,
        replace: boolean,
    ): Promise<ValidationError[]> {
        // 1. Snapshot current data and property state for rollback
        const prevAuthored = structuredClone(this._authored);
        const prevNames = this._propertyNames;
        const prevData = this._resolved;

        // 2. Apply: full replacement (snapshot restore) or shallow merge (patch).
        // Replacement drops keys missing from the snapshot; merge leaves them.
        if (replace) {
            this._authored = structuredClone(patch as TData);
        } else {
            Object.assign(this._authored, patch);
        }

        // 3. Validate the full merged data (supports in-place migration mutations)
        const errors = this.validate(this._authored);
        if (errors.length > 0) {
            this._authored = prevAuthored;
            return errors;
        }

        // 4. Re-scan property names and re-resolve
        this._rescanPropertyNames();
        this._resolved = this._resolveAll();

        // 5. Re-subscribe if property names changed
        const namesChanged = !_setsEqual(prevNames, this._propertyNames);
        if (namesChanged) {
            this._subscribeToProperties();
        }

        // 6. Apply side effects (subclass hook). Any change to an object's
        // behaviors may reshape what its bake holds (a size, a material, a
        // clip), so the bake is no longer the one it was loaded or saved with.
        markBakeDirty(this.sceneObject);
        try {
            await this.applyCommit(patch, prevAuthored);
        } catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            Logger.error(`[${this.constructor.name}] applyCommit failed: ${msg}`);
            this._authored = prevAuthored;
            this._propertyNames = prevNames;
            this._resolved = prevData;
            if (namesChanged) {
                this._subscribeToProperties();
            }
            try {
                this.onCommitRolledBack();
            } catch (hookError) {
                Logger.warn(`[${this.constructor.name}] onCommitRolledBack failed: ${String(hookError)}`);
            }
            return [{ message: msg, code: 'APPLY_FAILED' }];
        }

        // 7. Success
        return [];
    }

    /**
     * Apply side effects after data has been merged and validated.
     * Override in subclasses that need to react to data changes (e.g. update Babylon.js state).
     *
     * The base class handles merge, validation, and rollback. This hook is the single place
     * where behaviors push changes to the runtime.
     *
     * ## Error handling
     *
     * If this method throws, the base class rolls back authored data to `prev` and returns
     * `[{ code: 'APPLY_FAILED', message }]`. However, most behaviors that load assets
     * (Frame, AnimationSequence, ParticleSystem) catch load errors internally and degrade
     * gracefully — the data updates to the new value, old resources are disposed, and
     * runtime state (meshes, animation groups) becomes null. This is intentional:
     *
     * - **No cascading failures.** All code paths guard against null runtime state.
     * - **Recoverable.** The next commit with a valid asset URL triggers a reload.
     * - **Undo works.** Undo commits the previous (good) snapshot, which differs from
     *   the loaded-src tracker, triggering a successful reload.
     *
     * @param _patch - The partial data that was merged (check which keys are present)
     * @param _prev - The complete authored data snapshot before the merge (for diffing old vs new)
     */
    protected async applyCommit(_patch: Partial<TData>, _prev: TData): Promise<void> {}

    /**
     * Called after a failed `applyCommit` has been rolled back, with the
     * restored data already in place. Nothing else tells a behavior the
     * rollback happened, so anything it pushed to the runtime *before* the
     * failure is still describing data that no longer exists — a behavior that
     * publishes eagerly re-publishes here (Text's layout measurement).
     *
     * Synchronous, and a throw is logged rather than replacing the original
     * failure: the caller is already returning APPLY_FAILED.
     */
    protected onCommitRolledBack(): void {}

    /**
     * Serialize behavior data for export.
     * Returns a validated, normalized copy suitable for saving to .trace files.
     *
     * @returns A copy of the authored behavior data
     */
    public serialize(): TData {
        return structuredClone(this._authored);
    }

    /**
     * Validate and optionally migrate behavior data.
     * Override in subclasses for behavior-specific validation.
     *
     * Migrations should mutate the data object in-place before returning.
     *
     * @param data - Data to validate (may be mutated for migrations)
     * @returns Array of validation errors (empty if valid)
     * @protected
     */
    protected validate(_data: TData): ValidationError[] {
        return [];
    }

    /** The Babylon.js scene this behavior belongs to. */
    protected get scene(): Scene { return this.sceneObject.babylonScene; }

    /** The shared resource manager for loading assets. */
    protected get resources(): ResourceManager { return this.sceneObject.resources; }

    /** Initialize the behavior (called before attach). Override if needed. */
    init(): void {}

    /** Load any required resources. Override if needed. */
    load(): Promise<void> | void {}

    /**
     * Attaches this behavior to a target node.
     * Override onAttach() for custom attach logic.
     */
    attach(target: TransformNode): void {
        this.attachedNode = target;
        this._subscribeToProperties();
        this.onAttach(target);
        this._subscribePlayMode();
    }

    /**
     * Detaches this behavior from its target.
     * Override onDetach() for custom detach logic.
     */
    detach(): void {
        this._unsubscribePlayMode();
        this._unsubscribeFromProperties();
        this.onDetach();
        this.attachedNode = null;
    }

    /** Override for custom attach logic */
    protected onAttach(_target: TransformNode): void {}

    /** Override for custom detach logic */
    protected onDetach(): void {}

    /**
     * Called when play mode changes. Override to start/stop runtime effects.
     * The base class automatically subscribes to play mode events when this
     * method is overridden, and calls it with the current state on attach.
     */
    protected onPlayModeChanged(_playing: boolean): void {}

    /**
     * Called when this SceneObject receives a pointer event in play mode.
     * Override to respond to select/hover events without needing a
     * BehaviorGraph or a separate pointer observer.
     */
    public onPointerEvent(_type: 'select' | 'hover' | 'active', _isEnter: boolean): void {}

    /**
     * Whether this behavior acts on a play-mode select of its object. True
     * claims the tap: it fires here and does not bubble on to a descendant
     * listener, so a Pages deck does not also advance on it. Override it
     * together with an {@link onPointerEvent} that handles 'select'. Hover
     * and press handling alone leaves it false — that needs the object
     * pickable, not the tap. Keep it CHEAP — it runs per tap.
     */
    public claimsSelect(): boolean {
        return false;
    }

    /**
     * Whether this behavior listens to play-mode pointer events on DESCENDANT
     * objects (e.g. a Pages deck with `advanceOnTap`). Drives two things:
     * TraceScene's bubbling dispatch consults it at each ancestor hop, and
     * the play-mode pickability sweep makes content under a descendant
     * listener pickable at all (otherwise its taps read as misses and can
     * never bubble). Keep it CHEAP — it runs per tap and per sweep; never
     * deep-clone data here.
     */
    public wantsDescendantPointerEvents(): boolean {
        return false;
    }

    /**
     * Called — nearest ancestor first — when a descendant object receives a
     * play-mode pointer event that nothing closer has claimed: the tapped
     * object's own select handlers claim first, then each ancestor hop (an
     * ancestor a graph's onSelect listens on claims its subtree, mirroring
     * FlowGraph's descendant-matching dispatch). Return true to claim the
     * event and stop the walk; return false to let it keep bubbling.
     * Only consulted when {@link wantsDescendantPointerEvents} returns true.
     */
    public onDescendantPointerEvent(_type: 'select' | 'hover' | 'active', _isEnter: boolean, _target: SceneObject): boolean {
        return false;
    }

    /**
     * Called when this SceneObject's EFFECTIVE active state flips — own
     * enabled ∧ every ancestor's — no matter who flipped it (own `active`,
     * a state's `active: false`, a page hiding an ancestor, a script).
     * Fires on flips only; read `sceneObject.effectiveActive` for the
     * current state. Override for activation-lifecycle behavior (e.g.
     * AnimationSequence playing its authored enter/exit clips).
     */
    protected onEffectiveActiveChanged(_active: boolean): void {}

    /** Dispatch entry for SceneObject's effective-active tracking. @internal */
    _onEffectiveActiveChanged(active: boolean): void {
        this.onEffectiveActiveChanged(active);
    }

    /**
     * Returns true if the scene is currently in play mode.
     * Convenience accessor for use in commit() and other methods.
     */
    get playMode(): boolean {
        return this.sceneObject.traceScene.isPlayMode();
    }

    /**
     * Called when SceneObject creates an animation root.
     * Override to reparent non-Node visual content (e.g. TextRenderer)
     * that Babylon's getChildren() won't pick up automatically.
     */
    onAnimationRootCreated(_animationRoot: TransformNode): void {}

    /**
     * Called when a reparent that keeps this object where it is in the world
     * has re-expressed its local pose under another parent. `from` and `to`
     * are the world matrices of the parent frame it left and the one it is in
     * now; the bare root's is the identity. It runs in Play too, and a
     * microtask after the move when the move rests at the bare root.
     * Override to re-express a local pose the behavior holds itself.
     */
    onParentFrameChanged(_from: Matrix, _to: Matrix): void {}

    // ── Property subscription (auto-wired when authored data contains $name references) ──

    /** Tracks which PropertyProvider each name is subscribed to, for cleanup */
    private _subscribedProperties: Map<string, PropertyProvider> = new Map();

    /**
     * Subscribe to ancestor Properties for each name in `_propertyNames`.
     * Walks up the tree for each name and subscribes to the first defining Properties.
     */
    private _subscribeToProperties(): void {
        this._unsubscribeFromProperties();
        if (this._propertyNames.size === 0) return;

        for (const name of this._propertyNames) {
            const props = findPropertyProvider(this.sceneObject, name);
            if (props) {
                props.subscribe(name, this);
                this._subscribedProperties.set(name, props);
            }
        }
    }

    /** Unsubscribe from all property providers */
    private _unsubscribeFromProperties(): void {
        for (const [name, props] of this._subscribedProperties) {
            props.unsubscribe(name, this);
        }
        this._subscribedProperties.clear();
    }

    /**
     * Re-subscribe to properties and re-resolve `_resolved`.
     * Called when a new Properties behavior is added to an ancestor,
     * giving already-attached behaviors a chance to pick up the new source.
     */
    _resubscribeAndResolve(): void {
        if (this._propertyNames.size === 0) return;
        this._subscribeToProperties();
        const { patch, hasChanges } = this._reresolve();
        if (hasChanges) this._applyResolvedPatch(patch);
    }

    /**
     * Called by a PropertyProvider when a subscribed property value changes.
     * Re-resolves `_resolved` and calls `applyCommit()` with the changed fields.
     */
    _onPropertyChanged(_name: string): void {
        const { patch, hasChanges } = this._reresolve();
        if (hasChanges) this._applyResolvedPatch(patch);
    }

    /**
     * Re-resolve `_resolved` and diff it against the previous resolution:
     * the top-level fields whose resolved value changed. Shared by the
     * property-change path and subclasses that want to look at the diff
     * before deciding how to apply it (Properties: a bound base transform
     * re-applies without a commit).
     */
    protected _reresolve(): { patch: Partial<TData>; hasChanges: boolean } {
        const prevData = this._resolved;
        this._resolved = this._resolveAll();

        const changedPatch: Record<string, unknown> = {};
        let hasChanges = false;
        for (const key of Object.keys(this._resolved)) {
            if (key === '@type') continue;
            const oldVal = (prevData as Record<string, unknown>)[key];
            const newVal = (this._resolved as Record<string, unknown>)[key];
            if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
                changedPatch[key] = newVal;
                hasChanges = true;
            }
        }
        return { patch: changedPatch as Partial<TData>, hasChanges };
    }

    /** Hand a re-resolved patch to `applyCommit`. Authored data is unchanged, so prev === _authored. */
    protected _applyResolvedPatch(patch: Partial<TData>): void {
        markBakeDirty(this.sceneObject);
        this.applyCommit(patch, this._authored)
            .catch(e => Logger.warn(`[${this.name}] applyCommit failed during property change:`, e));
    }

    // ── Play mode subscription (auto-wired for subclasses that override onPlayModeChanged) ──

    private _playModeHandler: ((e: Event) => void) | null = null;

    private _subscribePlayMode(): void {
        // Only subscribe if the subclass actually overrides onPlayModeChanged
        if (this.onPlayModeChanged === TraceBehavior.prototype.onPlayModeChanged) return;

        const evt = this.sceneObject.traceScene.getEventSystem();
        if (!evt) return;

        this._playModeHandler = (e: Event) => {
            const playing = (e as CustomEvent<{ playMode: boolean }>).detail?.playMode;
            this.onPlayModeChanged(playing);
        };
        evt.addEventListener('trace-viewer:playmode-changed', this._playModeHandler);

        // Fire with current state so behaviors can start immediately if already playing
        if (this.sceneObject.traceScene.isPlayMode()) {
            this.onPlayModeChanged(true);
        }
    }

    private _unsubscribePlayMode(): void {
        if (!this._playModeHandler) return;
        const evt = this.sceneObject.traceScene.getEventSystem();
        if (evt) {
            evt.removeEventListener('trace-viewer:playmode-changed', this._playModeHandler);
        }
        this._playModeHandler = null;
    }
}
